import { slugify } from './stringUtils';
import { validateSubscriptionConfigurationAgainstForm } from './subscriptionConfigurationValidation';
import { collectJsonFieldErrors } from './jsonField';
import type { IntegrationSubscriptionCreatePayload } from '../types/integration';
import {
  coerceBooleanFieldValue,
  isNullishOrBlank,
  isRecord,
  parseConfigurationFormPages,
  type ConfigurationFormPageRecord,
} from './configurationNormalizationPrimitives';

/**
 * Data manipulation and processing utilities
 */

export { isRecord, isNullishOrBlank, parseConfigurationFormPages, type ConfigurationFormPageRecord };

/**
 * Narrow unknown values to traversable nodes (non-null objects, including arrays).
 */
export const isTraversable = (value: unknown): value is Record<string, unknown> | unknown[] => {
  return typeof value === 'object' && value !== null;
};

/**
 * Coerce numeric-like string values to numbers.
 * Leaves non-numeric strings and empty/nullish values unchanged.
 * Does not coerce values that would become non-finite or unsafe integers.
 */
export const coerceNumber = (raw: unknown): unknown => {
  if (raw === '' || raw === null || typeof raw === 'undefined') return raw;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    if (!raw.trim()) return raw;
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    if (Number.isInteger(n) && !Number.isSafeInteger(n)) return raw;
    return n;
  }
  return raw;
};

/**
 * Creates a deep copy of an object
 */
export const deepCopy = <T>(obj: T): T => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map((item) => deepCopy(item)) as T;
  }

  if (obj instanceof Object) {
    return Object.keys(obj).reduce(
      (newObj, key) => {
        newObj[key as keyof T] = deepCopy((obj as T)[key as keyof T]);
        return newObj;
      },
      {} as Record<keyof T, T[keyof T]>
    ) as T;
  }

  return obj;
};

/**
 * Checks if a string is valid JSON
 */
export const isJson = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * Safely parses a value that may be either a JSON string or already a parsed object.
 * Used during API transition where fields can be stringified or proper JSON.
 *
 * @param value - The value to parse (string or object/array)
 * @param fallback - Default value if parsing fails (default: null)
 * @returns The parsed value or fallback
 */
export function safeJsonParse<T>(value: string | T | null | undefined, fallback: T | null = null): T | null {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as T;
      return parsed === null ? fallback : parsed;
    } catch {
      return fallback;
    }
  }

  // Already an object/array
  return value as T;
}

/**
 * Normalizes array-shaped data when the API may return either array or object.
 * If an object is provided, its values are returned as an array.
 * If an array is provided, it is returned as-is.
 */
export function normalizeArray<T>(value: T[] | Record<string, T> | null | undefined): T[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return Object.values(value);
}

export { validateSubscriptionConfigurationAgainstForm };

export type ConfigurationItem = Record<string, unknown> & { page?: string };
type ConfigurationFormField = {
  type?: unknown;
  default?: unknown;
};
type ConfigurationFormPage = {
  title?: unknown;
  properties?: Record<string, ConfigurationFormField>;
};
type CredentialsFormField = {
  type?: unknown;
  default?: unknown;
};
type CredentialsFormSchema = {
  properties?: Record<string, CredentialsFormField>;
};
type NotificationTypeDefinition = {
  name?: unknown;
};
type NotifyByTypeEntry = {
  type?: unknown;
  name?: unknown;
  notify?: unknown;
};

const normalizeNotifyEntryValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map((entry) => (typeof entry === 'string' ? entry : '')).join(',');
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

/**
 * Normalizes subscription configuration data from the API.
 * Handles both stringified JSON and native JSON formats, and converts
 * object-shaped configuration to array format with page keys.
 *
 * @param configuration - Raw configuration from subscription (string or object/array)
 * @param integrationForm - The integration's configuration_form (string or object/array) used for page title fallbacks
 * @returns Normalized array of configuration objects with page properties
 */
export function normalizeSubscriptionConfiguration(configuration: unknown, integrationForm: unknown): ConfigurationItem[] {
  let parsedConfiguration = safeJsonParse<ConfigurationItem[] | Record<string, unknown>>(
    configuration as string | ConfigurationItem[] | Record<string, unknown>,
    []
  );

  // Parse the integration form for page title fallbacks (needed before object-to-array conversion)
  const formArray = normalizeArray<{ title?: string }>(
    safeJsonParse<Array<{ title?: string }> | Record<string, { title?: string }>>(
      integrationForm as string | Array<{ title?: string }> | Record<string, { title?: string }>,
      []
    )
  );

  // If it's an object, convert to array where each entry gets a .page property from its key,
  // ordered to match configuration_form page order (by slugified title).
  if (parsedConfiguration && !Array.isArray(parsedConfiguration) && typeof parsedConfiguration === 'object') {
    const configObject = parsedConfiguration as Record<string, unknown>;
    const newConfiguration: ConfigurationItem[] = [];
    const matchedKeys = new Set<string>();

    const convertEntry = (key: string): ConfigurationItem => {
      const parsedVal = safeJsonParse<ConfigurationItem>(configObject[key] as string | ConfigurationItem, {});
      let val: ConfigurationItem = parsedVal ?? {};
      if (Array.isArray(val) || (val && typeof val === 'object')) {
        val.page = key;
        return val as ConfigurationItem;
      }
      return { page: key };
    };

    // First, iterate form pages in order and match config keys by slugified title
    for (const formPage of formArray) {
      const title = typeof formPage?.title === 'string' ? formPage.title : '';
      const slug = title ? slugify(title) : '';
      if (!slug || matchedKeys.has(slug) || !Object.prototype.hasOwnProperty.call(configObject, slug)) {
        continue;
      }
      matchedKeys.add(slug);
      newConfiguration.push(convertEntry(slug));
    }

    // Append any remaining config keys not matched by form pages (backward compatibility)
    for (const key of Object.keys(configObject)) {
      if (!matchedKeys.has(key)) {
        newConfiguration.push(convertEntry(key));
      }
    }

    parsedConfiguration = newConfiguration;
  }

  if (!Array.isArray(parsedConfiguration)) {
    parsedConfiguration = [];
  }

  // Parse each configuration item and ensure it has a page property
  return parsedConfiguration.map((item, index) => {
    const parsedItem = safeJsonParse<ConfigurationItem>(item as string | ConfigurationItem, {});
    if (parsedItem && typeof parsedItem === 'object') {
      if (typeof parsedItem.page === 'undefined') {
        parsedItem.page = formArray[index]?.title || 'Page ' + (index + 1);
      }
      return parsedItem;
    }
    return { page: formArray[index]?.title || 'Page ' + (index + 1) };
  });
}

/**
 * Builds a fully-populated subscription configuration object keyed by page slug.
 * - Boolean/switch fields are always persisted as true/false.
 * - Missing non-boolean fields are persisted as null.
 */
export function buildSubscriptionConfigurationForSave(integrationForm: unknown, configuration: unknown): Record<string, Record<string, unknown>> {
  const integrationPages = normalizeArray<ConfigurationFormPage>(
    safeJsonParse<ConfigurationFormPage[] | Record<string, ConfigurationFormPage>>(
      integrationForm as string | ConfigurationFormPage[] | Record<string, ConfigurationFormPage>,
      []
    )
  );

  const parsedConfiguration = normalizeArray<unknown>(
    safeJsonParse<unknown[] | Record<string, unknown>>(configuration as string | unknown[] | Record<string, unknown>, [])
  ).map((item) => safeJsonParse<ConfigurationItem>(item as string | ConfigurationItem, {}) ?? {});

  const normalized: Record<string, Record<string, unknown>> = {};

  integrationPages.forEach((page, index) => {
    const pageTitle = typeof page?.title === 'string' && page.title.trim().length > 0 ? page.title : `Page ${index + 1}`;
    const pageKey = slugify(pageTitle) || `page-${index + 1}`;
    const pageProperties = isRecord(page?.properties) ? (page.properties as Record<string, ConfigurationFormField>) : {};
    const foundByPage = parsedConfiguration.find((item) => {
      const pageValue = item && typeof item === 'object' ? (item as ConfigurationItem).page : undefined;
      return typeof pageValue === 'string' && slugify(pageValue) === pageKey;
    });
    const sourceConfig =
      (isRecord(foundByPage) ? foundByPage : isRecord(parsedConfiguration[index]) ? (parsedConfiguration[index] as Record<string, unknown>) : {}) ??
      {};
    const nextPage: Record<string, unknown> = {};

    Object.entries(pageProperties).forEach(([fieldKey, fieldSchema]) => {
      const fieldType = typeof fieldSchema?.type === 'string' ? fieldSchema.type : '';
      const currentValue = sourceConfig[fieldKey];

      if (fieldType === 'boolean' || fieldType === 'switch') {
        const fallback = typeof fieldSchema?.default === 'boolean' ? fieldSchema.default : false;
        nextPage[fieldKey] = coerceBooleanFieldValue(currentValue, fallback);
        return;
      }

      if (typeof currentValue === 'undefined' || (typeof currentValue === 'string' && currentValue.trim().length === 0)) {
        nextPage[fieldKey] = null;
        return;
      }

      nextPage[fieldKey] = currentValue;
    });

    normalized[pageKey] = nextPage;
  });

  return normalized;
}

/**
 * What the API returns in place of a top-level credential whose key names a password, token, or secret, for users who
 * aren't superusers. Sending it back on save keeps the stored value, so it must reach the API unchanged, whatever the
 * field's type.
 */
export const MASKED_CREDENTIAL_VALUE = '********';

export const getCredentialsFormProperties = (credentialsForm: unknown): Record<string, CredentialsFormField> => {
  const parsedCredentialsForm = safeJsonParse<CredentialsFormSchema | CredentialsFormSchema[]>(
    credentialsForm as string | CredentialsFormSchema | CredentialsFormSchema[],
    {}
  );

  if (Array.isArray(parsedCredentialsForm)) {
    const merged: Record<string, CredentialsFormField> = {};
    parsedCredentialsForm.forEach((page) => {
      if (!isRecord(page?.properties)) {
        return;
      }
      Object.assign(merged, page.properties as Record<string, CredentialsFormField>);
    });
    return merged;
  }

  if (isRecord(parsedCredentialsForm?.properties)) {
    return parsedCredentialsForm.properties as Record<string, CredentialsFormField>;
  }

  return {};
};

/**
 * The credentials schema the Authorization tab renders: the first page's own keys (type, title, a dictionary's
 * `items`) plus every page's merged `properties`, so a credential is checked on save exactly when it is shown.
 */
export const getCredentialsFormSchema = (credentialsForm: unknown): Record<string, unknown> => {
  const firstPage = Array.isArray(credentialsForm) ? credentialsForm[0] : credentialsForm;
  const properties = getCredentialsFormProperties(credentialsForm);
  return {
    ...(isRecord(firstPage) ? firstPage : {}),
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
  };
};

/**
 * Whether the subscription edit pages show the Authorization tab: credentials or unset auth, or consent auth whose
 * credentials form defines fields (e.g. an API key next to the OAuth grant).
 */
export const showsAuthorizationTab = (integration: unknown): boolean => {
  const integrationRecord = isRecord(integration) ? integration : {};
  const authType = typeof integrationRecord.auth_type === 'string' ? integrationRecord.auth_type.trim().toLowerCase() : '';
  if (authType === '' || authType === 'credentials') return true;
  return authType === 'consent' && Object.keys(getCredentialsFormProperties(integrationRecord.credentials_form)).length > 0;
};

const normalizeFtpConnectionCredentialsForSave = (value: unknown): Record<string, unknown> => {
  const parsedConnection = safeJsonParse<Record<string, unknown>>(value as string | Record<string, unknown>, {}) ?? {};
  const sourceConnection = isRecord(parsedConnection) ? parsedConnection : {};
  const protocolValue = sourceConnection.protocol;
  const normalizedProtocol = typeof protocolValue === 'string' && protocolValue.trim().length > 0 ? protocolValue : 'sftp';
  const normalizedPortValue = coerceNumber(sourceConnection.port);
  const normalizedPort = typeof normalizedPortValue === 'number' ? normalizedPortValue : 22;
  return {
    ...sourceConnection,
    protocol: normalizedProtocol,
    port: normalizedPort,
  };
};

const normalizeAs2ConnectionCredentialsForSave = (value: unknown): Record<string, unknown> => {
  const parsedConnection = safeJsonParse<Record<string, unknown>>(value as string | Record<string, unknown>, {}) ?? {};
  const sourceConnection = isRecord(parsedConnection) ? parsedConnection : {};
  return { ...sourceConnection, protocol: 'as2' };
};

/**
 * Builds a fully-populated subscription credentials object from credentials_form.
 * - A value the API masked (MASKED_CREDENTIAL_VALUE) is sent back unchanged, so the API keeps the stored value.
 * - Boolean/switch fields are always persisted as true/false.
 * - Missing non-boolean fields are persisted as null.
 */
export function buildSubscriptionCredentialsForSave(credentialsForm: unknown, credentials: unknown): Record<string, unknown> {
  const formProperties = getCredentialsFormProperties(credentialsForm);
  const parsedCredentials = safeJsonParse<Record<string, unknown>>(credentials as string | Record<string, unknown>, {}) ?? {};
  const sourceCredentials = isRecord(parsedCredentials) ? parsedCredentials : {};
  const normalizedCredentials: Record<string, unknown> = { ...sourceCredentials };

  Object.entries(formProperties).forEach(([fieldKey, fieldSchema]) => {
    const fieldType = typeof fieldSchema?.type === 'string' ? fieldSchema.type : '';
    const currentValue = sourceCredentials[fieldKey];

    if (currentValue === MASKED_CREDENTIAL_VALUE) {
      return;
    }

    if (fieldType === 'boolean' || fieldType === 'switch') {
      const fallback = typeof fieldSchema?.default === 'boolean' ? fieldSchema.default : false;
      normalizedCredentials[fieldKey] = coerceBooleanFieldValue(currentValue, fallback);
      return;
    }

    if (fieldType === 'ftp_connection') {
      normalizedCredentials[fieldKey] = normalizeFtpConnectionCredentialsForSave(currentValue);
      return;
    }

    if (fieldType === 'as2_connection') {
      normalizedCredentials[fieldKey] = normalizeAs2ConnectionCredentialsForSave(currentValue);
      return;
    }

    if (typeof currentValue === 'undefined' || (typeof currentValue === 'string' && currentValue.trim().length === 0)) {
      normalizedCredentials[fieldKey] = null;
      return;
    }

    normalizedCredentials[fieldKey] = currentValue;
  });

  return normalizedCredentials;
}

/**
 * Builds notify_by_type from integration notification_types so every defined type is persisted.
 */
export function buildSubscriptionNotifyByTypeForSave(notificationTypes: unknown, notifyByType: unknown): Array<{ type: string; notify: string }> {
  const typeDefinitions = normalizeArray<NotificationTypeDefinition>(
    safeJsonParse<NotificationTypeDefinition[] | Record<string, NotificationTypeDefinition>>(
      notificationTypes as string | NotificationTypeDefinition[] | Record<string, NotificationTypeDefinition>,
      []
    )
  );
  const notifyEntries = normalizeArray<NotifyByTypeEntry>(
    safeJsonParse<NotifyByTypeEntry[] | Record<string, NotifyByTypeEntry>>(
      notifyByType as string | NotifyByTypeEntry[] | Record<string, NotifyByTypeEntry>,
      []
    )
  );

  const notifyByTypeLookup = new Map<string, string>();
  notifyEntries.forEach((entry) => {
    const entryType = typeof entry?.type === 'string' ? entry.type : typeof entry?.name === 'string' ? entry.name : '';
    if (!entryType) {
      return;
    }
    notifyByTypeLookup.set(entryType, normalizeNotifyEntryValue(entry?.notify));
  });

  return typeDefinitions
    .map((definition) => (typeof definition?.name === 'string' ? definition.name.trim() : ''))
    .filter((name): name is string => name.length > 0)
    .map((typeName) => ({
      type: typeName,
      notify: notifyByTypeLookup.get(typeName) ?? '',
    }));
}

export function hasNotificationTypesSchema(notificationTypes: unknown): boolean {
  if (notificationTypes === null || typeof notificationTypes === 'undefined') {
    return false;
  }
  if (typeof notificationTypes === 'string' && notificationTypes.trim().length === 0) {
    return false;
  }

  const parsedDefinitions = normalizeArray<NotificationTypeDefinition>(
    safeJsonParse<NotificationTypeDefinition[] | Record<string, NotificationTypeDefinition>>(
      notificationTypes as string | NotificationTypeDefinition[] | Record<string, NotificationTypeDefinition>,
      []
    )
  );

  return parsedDefinitions.some((definition) => {
    return typeof definition?.name === 'string' && definition.name.trim().length > 0;
  });
}

export type NormalizedSubscriptionSaveFields = {
  configuration: Record<string, unknown> | Record<string, unknown>[];
  credentials: Record<string, unknown>;
  notify_by_type: Array<{ type: string; notify: string }>;
};

type NormalizedSubscriptionSaveSuccess = {
  ok: true;
  payload: NormalizedSubscriptionSaveFields;
  warnings: string[];
};

type NormalizedSubscriptionSaveFailure = {
  ok: false;
  errors: string[];
  warnings: string[];
};

export type NormalizedSubscriptionSaveResult = NormalizedSubscriptionSaveSuccess | NormalizedSubscriptionSaveFailure;

/**
 * Builds one canonical normalized payload shape for integration subscription saves.
 * Returns JSON-native configuration/credentials/notify_by_type values or a validation error.
 */
export function buildNormalizedSubscriptionSavePayload(integration: unknown, subscriptionDraft: unknown): NormalizedSubscriptionSaveResult {
  const integrationRecord = isRecord(integration) ? integration : {};
  const subscriptionRecord = isRecord(subscriptionDraft) ? subscriptionDraft : {};
  const rawConfiguration = subscriptionRecord.configuration;
  const rawCredentials = subscriptionRecord.credentials;
  const rawNotifyByType = subscriptionRecord.notify_by_type;
  const parsedConfigurationForm = parseConfigurationFormPages(integrationRecord.configuration_form);
  const validation = validateSubscriptionConfigurationAgainstForm(integrationRecord.configuration_form, rawConfiguration);
  if (validation.errors.length > 0) {
    return { ok: false, errors: validation.errors, warnings: validation.warnings };
  }

  if (typeof rawCredentials === 'string' && rawCredentials.trim() !== '' && !isJson(rawCredentials)) {
    return { ok: false, errors: ['Credentials JSON is invalid'], warnings: validation.warnings };
  }

  const configurationObject =
    parsedConfigurationForm.pages.length > 0
      ? validation.normalizedConfigurationByPage
      : safeJsonParse(rawConfiguration as string | Record<string, unknown> | unknown[], {});

  const credentialsObject =
    typeof integrationRecord.credentials_form !== 'undefined'
      ? buildSubscriptionCredentialsForSave(integrationRecord.credentials_form, rawCredentials)
      : safeJsonParse(rawCredentials as string | Record<string, unknown>, {});

  // Check json and json_query credentials (nested ones too) without changing them. A hidden Authorization tab is
  // skipped: its values could never be fixed, so they must not block every save.
  if (typeof integrationRecord.credentials_form !== 'undefined' && showsAuthorizationTab(integrationRecord)) {
    // A top-level value the API masked goes back unchanged, and the API then keeps the stored value, so it is not checked.
    const credentialValues = isRecord(credentialsObject)
      ? Object.fromEntries(Object.entries(credentialsObject).filter(([, value]) => value !== MASKED_CREDENTIAL_VALUE))
      : {};
    // Walk the schema the tab renders from its root, so a dictionary form's `items` is checked as well as properties.
    const credentialErrors = collectJsonFieldErrors(getCredentialsFormSchema(integrationRecord.credentials_form), credentialValues, 'credentials');
    if (credentialErrors.length > 0) {
      return { ok: false, errors: credentialErrors, warnings: validation.warnings };
    }
  }

  const notifyByTypeValue = hasNotificationTypesSchema(integrationRecord.notification_types)
    ? buildSubscriptionNotifyByTypeForSave(integrationRecord.notification_types, rawNotifyByType)
    : normalizeArray(safeJsonParse(rawNotifyByType as string | NotifyByTypeEntry[] | Record<string, NotifyByTypeEntry>, []));

  return {
    ok: true,
    warnings: validation.warnings,
    payload: {
      configuration: (configurationObject ?? {}) as Record<string, unknown> | Record<string, unknown>[],
      credentials: (credentialsObject ?? {}) as Record<string, unknown>,
      notify_by_type: notifyByTypeValue as Array<{ type: string; notify: string }>,
    },
  };
}

type NormalizedSubscriptionSaveConsumerSuccess = {
  ok: true;
  payload: NormalizedSubscriptionSaveFields;
  warningMessage: string | null;
};

type NormalizedSubscriptionSaveConsumerFailure = {
  ok: false;
  errorMessage: string;
};

export type NormalizedSubscriptionSaveConsumerResult = NormalizedSubscriptionSaveConsumerSuccess | NormalizedSubscriptionSaveConsumerFailure;

export const formatNormalizedSubscriptionSaveWarnings = (warnings: string[]): string | null => {
  if (warnings.length === 0) {
    return null;
  }

  const firstWarning = warnings[0];
  const additionalCount = warnings.length - 1;
  if (additionalCount === 0) {
    return firstWarning;
  }

  return `${firstWarning} (+${additionalCount} more warning${additionalCount > 1 ? 's' : ''})`;
};

export const consumeNormalizedSubscriptionSaveResult = (
  result: NormalizedSubscriptionSaveResult,
  fallbackErrorMessage = 'Subscription configuration is invalid'
): NormalizedSubscriptionSaveConsumerResult => {
  if (!result.ok) {
    return {
      ok: false,
      errorMessage: result.errors[0] || fallbackErrorMessage,
    };
  }

  return {
    ok: true,
    payload: result.payload,
    warningMessage: formatNormalizedSubscriptionSaveWarnings(result.warnings),
  };
};

/**
 * Build create payload from normalized JSON-native values.
 */
export const buildCreateSubscriptionPayloadFromNormalized = (
  integrationId: number,
  normalizedPayload: NormalizedSubscriptionSaveFields
): IntegrationSubscriptionCreatePayload => {
  return {
    integration_id: integrationId,
    credentials: normalizedPayload.credentials,
    configuration: normalizedPayload.configuration,
    notify_by_type: normalizedPayload.notify_by_type,
  };
};

/**
 * Reorders object keys according to specified order
 */
export const objectOrder = <T extends Record<string, unknown>>(obj: T, order: (keyof T)[]): Partial<T> => {
  const newObject: Partial<T> = {};
  for (let i = 0; i < order.length; i++) {
    if (obj.hasOwnProperty(order[i])) {
      newObject[order[i]] = obj[order[i]];
    }
  }
  return newObject;
};
